import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormSection, FormField } from '../types';
import FormSectionComponent from '../section/FormSection';

import { generateUUID } from '@/lib/utils/uuid';
import {  AVAILABLE_FIELDS } from '@/lib/utils/fieldDefinitions';
import { useLensesOptional } from '@/contexts/LensesContext';

export interface BlueprintSectionsManagerProps {
  sections: FormSection[];
  renderOrder: Array<{ type: 'section' | 'note'; id: string }>;
  onSectionsChange: (sections: FormSection[]) => void;
  onRenderOrderChange: (order: Array<{ type: 'section' | 'note'; id: string }>) => void;
  onDeleteSection: (sectionId: string) => void;
  onUpdateSection: (sectionId: string, section: FormSection) => void;
  isEditingExisting?: boolean;
  lastAddedSectionId?: string; // ID of the last section that was added by user
  lastAddedFieldId?: string; // ID of the last field that was added by user  
  onClearLastAddedFieldId?: () => void; // Function to clear the last added field ID
  disabled?: boolean;
  showDocumentation?: boolean; // Whether to show documentation inline
  onSectionVisibilityChange?: (sectionId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
  onFieldVisibilityChange?: (sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
}

// Sortable section item component
const SortableSectionItem = ({
  section,
  onDelete,
  onUpdate,
  onAddField,
  onRemoveField,
  onUpdateField,
  onFieldDragEnd,
  onUpdateSectionTitle,
  setSectionRef,
  lastAddedFieldId,
  onClearLastAddedFieldId,
  disabled,
  showDocumentation,
  totalSections,
  previewMode,
  previewRole,
  onSectionVisibilityChange,
  onFieldVisibilityChange,
}: {
  section: FormSection;
  onDelete: (id: string) => void;
  onUpdate: (id: string, section: FormSection) => void;
  onAddField: (sectionId: string, fieldType: string) => void;
  onRemoveField: (sectionId: string, fieldId: string) => void;
  onUpdateField: (sectionId: string, fieldId: string, updates: Partial<FormField>) => void;
  onFieldDragEnd: () => void;
  onUpdateSectionTitle: (sectionId: string, title: string) => void;
  setSectionRef: (id: string, element: HTMLElement | null) => void;
  lastAddedFieldId?: string;
  onClearLastAddedFieldId?: () => void;
  disabled?: boolean;
  showDocumentation?: boolean;
  totalSections: number;
  previewMode?: boolean;
  previewRole?: string | null;
  onSectionVisibilityChange?: (sectionId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
  onFieldVisibilityChange?: (sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Combined ref callback to handle both sortable and scroll refs
  const combinedRef = React.useCallback((element: HTMLElement | null) => {
    setNodeRef(element);
    setSectionRef(section.id, element);
  }, [setNodeRef, setSectionRef, section.id]);

  return (
    <div ref={combinedRef} style={style}>
      <FormSectionComponent
        section={section}
        sectionIndex={0}
        onAddField={(sectionId) => onAddField(sectionId, 'title')}
        onRemoveSection={onDelete}
        onUpdateField={(sectionId, fieldId, updates) => {
          onUpdateField(sectionId, fieldId, updates);
        }}
        onUpdateSectionTitle={onUpdateSectionTitle}
        onRemoveField={onRemoveField}
        onFieldDragEnd={onFieldDragEnd}
        dragHandleProps={{
          attributes,
          listeners,
        }}
        lastAddedFieldId={lastAddedFieldId}
        onClearLastAddedFieldId={onClearLastAddedFieldId}
        disabled={disabled}
        showDocumentation={showDocumentation}
        onUpdate={onUpdate}
        totalSections={totalSections}
        previewMode={previewMode}
        previewRole={previewRole}
        onSectionVisibilityChange={onSectionVisibilityChange}
        onFieldVisibilityChange={onFieldVisibilityChange}
      />
    </div>
  );
};

export default function BlueprintSectionsManager({
  sections,
  renderOrder,
  onSectionsChange,
  onRenderOrderChange,
  onDeleteSection,
  onUpdateSection,
  isEditingExisting = false,
  lastAddedSectionId,
  lastAddedFieldId,
  onClearLastAddedFieldId,
  disabled = false,
  showDocumentation = false,
  onSectionVisibilityChange,
  onFieldVisibilityChange,
}: BlueprintSectionsManagerProps) {
  const [activeSection, setActiveSection] = React.useState<FormSection | null>(null);
  const [activeField, setActiveField] = React.useState<FormField | null>(null);
  const [activeSectionId, setActiveSectionId] = React.useState<string | null>(null);
  const sectionRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const prevLastAddedSectionId = React.useRef<string | undefined>(undefined);
  
  // Get lenses context for role-based filtering
  const lensesContext = useLensesOptional();
  const previewMode = lensesContext?.previewMode ?? false;
  const previewRole = lensesContext?.selectedRole ?? null;
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Effect to scroll to newly added sections (only when user-triggered)
  React.useEffect(() => {
    // Only scroll if lastAddedSectionId changed and is not undefined
    if (lastAddedSectionId && lastAddedSectionId !== prevLastAddedSectionId.current) {
      const element = sectionRefs.current.get(lastAddedSectionId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 100); // Small delay to ensure the element is rendered
      }
      prevLastAddedSectionId.current = lastAddedSectionId;
    }
  }, [lastAddedSectionId]);

  // Function to register section refs
  const setSectionRef = React.useCallback((id: string, element: HTMLElement | null) => {
    if (element) {
      sectionRefs.current.set(id, element);
    } else {
      sectionRefs.current.delete(id);
    }
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    
    // Check if dragging a section
    const section = sections.find(s => s.id === active.id);
    if (section) {
      setActiveSection(section);
      return;
    }
    
    // Check if dragging a field
    for (const section of sections) {
      const field = section.fields.find(f => f.id === active.id);
      if (field) {
        setActiveField(field);
        setActiveSectionId(section.id);
        return;
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    
    if (!over) return;
    
    // Only handle field dragging
    if (activeField && activeSectionId) {
      // Find which section the 'over' item belongs to
      let overSectionId: string | null = null;
      let overFieldId: string | null = null;
      
      // Check if over is a section
      if (sections.find(s => s.id === over.id)) {
        overSectionId = over.id as string;
      } else {
        // Check if over is a field
        for (const section of sections) {
          if (section.fields.find(f => f.id === over.id)) {
            overSectionId = section.id;
            overFieldId = over.id as string;
            break;
          }
        }
      }
      
      // If we're dragging over a different section
      if (overSectionId && overSectionId !== activeSectionId) {
        const activeSection = sections.find(s => s.id === activeSectionId);
        const overSection = sections.find(s => s.id === overSectionId);
        
        if (activeSection && overSection) {
          // Remove field from active section
          const fieldToMove = activeSection.fields.find(f => f.id === active.id);
          if (!fieldToMove) return;
          
          const newActiveFields = activeSection.fields.filter(f => f.id !== active.id);
          const newActiveFieldOrder = activeSection.fieldOrder.filter(id => id !== active.id);
          
          // Add field to over section
          let newOverFields: FormField[];
          let newOverFieldOrder: string[];
          
          if (overFieldId) {
            // Insert at the position of the field we're over
            const overIndex = overSection.fields.findIndex(f => f.id === overFieldId);
            newOverFields = [
              ...overSection.fields.slice(0, overIndex),
              fieldToMove,
              ...overSection.fields.slice(overIndex)
            ];
            newOverFieldOrder = [
              ...overSection.fieldOrder.slice(0, overIndex),
              fieldToMove.id,
              ...overSection.fieldOrder.slice(overIndex)
            ];
          } else {
            // Add to the end of the section
            newOverFields = [...overSection.fields, fieldToMove];
            newOverFieldOrder = [...overSection.fieldOrder, fieldToMove.id];
          }
          
          // Update both sections
          const updatedSections = sections.map(section => {
            if (section.id === activeSectionId) {
              return {
                ...section,
                fields: newActiveFields,
                fieldOrder: newActiveFieldOrder,
              };
            }
            if (section.id === overSectionId) {
              return {
                ...section,
                fields: newOverFields,
                fieldOrder: newOverFieldOrder,
              };
            }
            return section;
          });
          
          onSectionsChange(updatedSections);
          setActiveSectionId(overSectionId);
        }
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Check if we're dragging a section
      if (activeSection) {
        const oldIndex = renderOrder.findIndex(item => item.id === active.id);
        const newIndex = renderOrder.findIndex(item => item.id === over.id);

        const newOrder = arrayMove(renderOrder, oldIndex, newIndex);
        onRenderOrderChange(newOrder);
      }
      
      // Check if we're dragging a field within the same section
      if (activeField && activeSectionId) {
        const section = sections.find(s => s.id === activeSectionId);
        if (!section) return;

        // Check if both active and over are in the same section
        const overField = section.fields.find(f => f.id === over.id);
        if (overField) {
          const oldIndex = section.fields.findIndex(field => field.id === active.id);
          const newIndex = section.fields.findIndex(field => field.id === over.id);

          const newFields = arrayMove(section.fields, oldIndex, newIndex);
          const newFieldOrder = newFields.map(field => field.id);

          const updatedSection = {
            ...section,
            fields: newFields,
            fieldOrder: newFieldOrder,
          };

          onUpdateSection(activeSectionId, updatedSection);
        }
      }
    }
    
    setActiveSection(null);
    setActiveField(null);
    setActiveSectionId(null);
  };

  const handleAddField = (sectionId: string, fieldType: string) => {
    // Get the first available field type as default if fieldType is not valid
    const defaultFieldType = Object.keys(AVAILABLE_FIELDS)[0] as keyof typeof AVAILABLE_FIELDS;
    const validFieldType = AVAILABLE_FIELDS[fieldType] ? fieldType as keyof typeof AVAILABLE_FIELDS : defaultFieldType;
    
    const fieldDef = AVAILABLE_FIELDS[validFieldType];
    
    const newField: FormField = {
      id: generateUUID(),
      type: validFieldType,
      label: fieldDef.label,
      mandatory: fieldDef.required || false,
      translation: false,
      multiple: false,
      filter: false,
    };

    const updatedSections = sections.map(section =>
      section.id === sectionId
        ? { 
            ...section, 
            fields: [...section.fields, newField],
            fieldOrder: [...section.fieldOrder, newField.id]
          }
        : section
    );
    onSectionsChange(updatedSections);
  };

  const handleDeleteField = (sectionId: string, fieldId: string) => {
    const updatedSections = sections.map(section =>
      section.id === sectionId
        ? { 
            ...section, 
            fields: section.fields.filter(field => field.id !== fieldId),
            fieldOrder: section.fieldOrder.filter(id => id !== fieldId)
          }
        : section
    );

    onSectionsChange(updatedSections);
  };

  const handleUpdateField = (sectionId: string, fieldId: string, updates: Partial<FormField>) => {
    const updatedSections = sections.map(section =>
      section.id === sectionId
        ? {
            ...section,
            fields: section.fields.map(field =>
              field.id === fieldId ? { ...field, ...updates } : field
            ),
          }
        : section
    );

    onSectionsChange(updatedSections);
  };

  const handleUpdateSection = (sectionId: string, updatedSection: FormSection) => {
    onUpdateSection(sectionId, updatedSection);
  };

  // Filter sections based on role visibility
  const filteredSections = React.useMemo(() => {
    if (!previewMode || !previewRole) {
      return sections;
    }
    
    const filtered = sections.filter(section => {
      // Hide section if it's hidden for the preview role
      if (section.hiddenForRoles?.includes(previewRole)) {
        return false;
      }
      return true;
    });
    
    return filtered;
  }, [sections, previewMode, previewRole]);

  // Create a flat list of all draggable items (sections + all fields)
  const allDraggableIds = React.useMemo(() => {
    const sectionIds = renderOrder.filter(item => item.type === 'section').map(item => item.id);
    const fieldIds = filteredSections.flatMap(section => section.fields.map(field => field.id));
    return [...sectionIds, ...fieldIds];
  }, [filteredSections, renderOrder]);

  return (
    <div className="space-y-4">
     
      
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={allDraggableIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4 mb-10">
            {renderOrder
              .filter(item => item.type === 'section')
              .map(item => {
                const section = filteredSections.find(s => s.id === item.id);
                if (!section) return null;

                return (
                  <SortableSectionItem
                    key={section.id}
                    section={section}
                    onDelete={onDeleteSection}
                    onUpdate={handleUpdateSection}
                    onAddField={handleAddField}
                    onRemoveField={handleDeleteField}
                    onUpdateField={handleUpdateField}
                    onFieldDragEnd={() => {}}
                    onUpdateSectionTitle={(sectionId, title) => {
                      const section = sections.find(s => s.id === sectionId);
                      if (section) {
                        handleUpdateSection(sectionId, { ...section, title });
                      }
                    }}
                    setSectionRef={setSectionRef}
                    lastAddedFieldId={lastAddedFieldId}
                    onClearLastAddedFieldId={onClearLastAddedFieldId}
                    disabled={disabled}
                    showDocumentation={showDocumentation}
                    totalSections={sections.length}
                    previewMode={previewMode}
                    previewRole={previewRole}
                    onSectionVisibilityChange={onSectionVisibilityChange}
                    onFieldVisibilityChange={onFieldVisibilityChange}
                  />
                );
              })}
          </div>
        </SortableContext>
        
        <DragOverlay>
          {activeSection ? (
            <div className="opacity-90 transform rotate-2 shadow-2xl pointer-events-none">
              <FormSectionComponent
                section={activeSection}
                sectionIndex={0}
                onAddField={() => {}}
                onRemoveSection={() => {}}
                onUpdateField={() => {}}
                onUpdateSectionTitle={() => {}}
                onRemoveField={() => {}}
                onFieldDragEnd={() => {}}
                totalSections={sections.length}
              />
            </div>
          ) : activeField ? (
            <div className="opacity-90 transform rotate-1 shadow-2xl pointer-events-none bg-card p-2 rounded border border-border">
              <div className="text-sm font-medium text-foreground">{activeField.label}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

    </div>
  );
} 